/**
 * useResultCategoryResolver
 *
 * Gives a surface the one category rule (`resolveResultCategory`) already
 * bound to the input every surface must share: the timing of each result,
 * read from her cycles. Home cards, All results, a cycle's tests,
 * the AMH and test-panel pages and the documents list all categorize through
 * this, so one result sits in one place.
 */

import { useMemo } from "react";
import {
  useActiveCycleWithPhases,
  useCycles,
} from "@src/packages/cycles/hooks/useCycles";
import type { ResultPhaseLike } from "../interpretation-context";
import {
  createResultCategoryResolver,
  type ResultCategoryResolver,
} from "../result-category";

export type { ResultCategoryResolver };

const NO_PHASES: ResultPhaseLike[] = [];

export function useResultCategoryResolver(): ResultCategoryResolver {
  const activeCycle = useActiveCycleWithPhases();
  const { data: cycles } = useCycles();
  const activeCycleId = activeCycle.cycle?.id ?? null;
  const activeCycleStartDate = activeCycle.cycle?.startDate ?? null;
  // `phases` is a fresh empty array while the query has no data; keep one
  // reference so the resolver stays stable across renders.
  const activePhases =
    activeCycle.phases.length > 0 ? activeCycle.phases : NO_PHASES;

  return useMemo(() => {
    const cycleStartDates: Record<string, string | null> = {};
    for (const cycle of cycles ?? []) {
      cycleStartDates[cycle.id] = cycle.startDate ?? null;
    }
    return createResultCategoryResolver({
      sources: {
        activeCycle: activeCycleId
          ? {
              id: activeCycleId,
              startDate: activeCycleStartDate,
              phases: activePhases,
            }
          : null,
        cycleStartDates,
      },
    });
  }, [activeCycleId, activeCycleStartDate, activePhases, cycles]);
}
